// src/pages/Checkout.js

import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
    ShoppingCartIcon, 
    EnvelopeIcon, 
    PaperAirplaneIcon, 
    CheckCircleIcon,
    CreditCardIcon,
    GlobeAltIcon,
    BuildingLibraryIcon
} from '@heroicons/react/24/solid';

// Import necessary modules from the BasharSelector's context
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';
import UrlConfig from '../../data/UrlConfig';

// Helper function to parse query params
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// Product details mapping
const productDetails = {
  'gamma-masterclass': { name: 'The "Going Gamma" Masterclass' },
  'fractal-system': { name: 'The Complete Fractal System' },
  'kaleidoscope-meditation': { name: 'The Kaleidoscope Meditation' },
  'irresistible-bundle': { name: 'The Entire Bundle' },
};

// Payment methods data
const paymentMethods = [
    { id: 'mercado_pago', name: 'Mercado Pago', description: 'Credit Card Link', Icon: CreditCardIcon },
    { id: 'payoneer', name: 'Payoneer', description: 'Payment Link', Icon: GlobeAltIcon },
    { id: 'bank_transfer', name: 'Bank Transfer', description: 'Instructions via Email', Icon: BuildingLibraryIcon },
];

const Checkout = () => {
  const query = useQuery();
  const location = useLocation();

  // State
  const [productId, setProductId] = useState('');
  const [price, setPrice] = useState('');
  const [productName, setProductName] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [email, setEmail] = useState(localStorage.getItem('track_user_email') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const pid = query.get('product_id');
    const p = query.get('price');
    if (pid && p && productDetails[pid]) {
      setProductId(pid);
      setPrice(p);
      setProductName(productDetails[pid].name);
    }
  }, [location, query]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!selectedPaymentMethod) {
        setError('Please select a payment method.');
        return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address.');
        setIsLoading(false);
        return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(UrlConfig.leads_action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          nick: email, // Using email for 'nick' as no separate name input
          page: 'CheckoutPage', // Specific value for this page
          folder: 'checkout',
          section: 'complete-payment-request', // Specific section
          status: 'OfferPurchaseInterest', // Specific status for purchase interest
          newsletter: [productId, selectedPaymentMethod, parseFloat(price)], // Sending single productId as an array
          totalCost: parseFloat(price), // Ensure price is a number
          currency: 'USD',
          paymentMethod: selectedPaymentMethod, // Include selected payment method
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Log submission error
        sendCodedLog({
            message: `Failed to submit checkout request for product: ${productName}.`,
            event: 'checkout_page_error',
            userName: email,
            userEmail: email,
            productId: productId,
            productName: productName,
            price: price,
            paymentMethod: selectedPaymentMethod,
            errorMessage: data.message || 'Unknown error',
            ...Common.getCommonLogData(location)
        });
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }
      
      setIsSubmitted(true);
      // Log successful submission
      sendCodedLog({
          message: `User successfully submitted checkout request for product: ${productName}.`,
          event: 'checkout_page_success',
          userName: email,
          userEmail: email,
          productId: productId,
          productName: productName,
          price: price,
          paymentMethod: selectedPaymentMethod,
          ...Common.getCommonLogData(location)
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // --- Dynamic Text Variables ---
  // These variables will change based on the user's selection.
  const isBankTransfer = selectedPaymentMethod === 'bank_transfer';
  const formHeaderText = isBankTransfer 
    ? "2. Enter your email to receive instructions" 
    : "2. Enter your email to receive the payment link";
  const formButtonText = isBankTransfer 
    ? "Send Payment Instructions" 
    : "Send Payment Link";

  // --- Render Logic ---
  if (!productId) {
    return (
      <div className="bg-black text-white min-h-screen flex flex-col items-center justify-center text-center p-4">
        <h1 className="text-3xl font-bold mb-4">No Offer Selected</h1>
        <p className="text-gray-400 mb-8">Please go back and choose an offer to proceed.</p>
        <Link to="/" className="py-2 px-6 rounded-lg bg-gray-800/50 border border-gray-700 font-semibold hover:bg-gray-800">Back to Home</Link>
      </div>
    );
  }
  
  if (isSubmitted) {
    return (
      <div className="bg-black text-white min-h-screen flex flex-col items-center justify-center text-center p-4">
        <CheckCircleIcon className="w-16 h-16 text-green-500 mb-6" />
        <h1 className="text-4xl font-bold mb-4">Request Received!</h1>
        <p className="text-lg text-gray-300 max-w-xl mx-auto">
            Your payment instructions will be sent to <strong className="text-yellow-400">{email}</strong> shortly. Please check your inbox.
        </p>
      </div>
    );
  }

  return (
    <div className="relative bg-black text-white min-h-screen font-sans flex flex-col justify-center items-center p-4">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95 z-0"></div>
        <main className="relative z-10 w-full max-w-2xl">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 md:p-12 shadow-2xl backdrop-blur-sm">
                
                <div className="text-center mb-8">
                    <ShoppingCartIcon className="w-12 h-12 mx-auto mb-4 text-yellow-400"/>
                    <h1 className="text-3xl font-bold">Complete Your Order</h1>
                </div>

                <div className="bg-black/40 p-4 rounded-lg border border-gray-700 mb-8 text-center">
                    <p className="text-gray-400">You have selected:</p>
                    <p className="text-xl font-semibold text-white mt-1">{productName} - <span className="text-yellow-400">${price}</span></p>
                </div>
                
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-center mb-4 text-gray-300">1. Choose your payment method</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {paymentMethods.map((method) => (
                            <button
                                data-track-click="select-payment-method"
                                key={method.id}
                                onClick={() => setSelectedPaymentMethod(method.id)}
                                className={`p-4 rounded-lg border-2 text-center transition-all duration-200 ${
                                    selectedPaymentMethod === method.id 
                                    ? 'bg-gray-700/50 border-yellow-500 scale-105' 
                                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-500'
                                }`}
                            >
                                <method.Icon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                <span className="block font-semibold">{method.name}</span>
                                <span className="block text-xs text-gray-400">{method.description}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {selectedPaymentMethod && (
                    <div className="p-6 bg-black/30 rounded-lg border border-gray-800 animate-fade-in">
                        <form onSubmit={handleSubmit}>
                            <h2 className="text-lg font-semibold text-center mb-4 text-gray-300">{formHeaderText}</h2>
                            <div className="relative">
                                <EnvelopeIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
                                <input
                                    data-track-click="submit-payment-method"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg py-3 pl-10 pr-4 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition"
                                    required
                                />
                            </div>
                            {error && <p className="text-red-500 text-sm mt-2 text-center">{error}</p>}
                            <button 
                                type="submit" 
                                disabled={isLoading} 
                                className="w-full mt-6 py-3 px-6 rounded-lg bg-yellow-500 text-black font-bold text-lg hover:bg-yellow-400 transition-all flex items-center justify-center disabled:bg-gray-600 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Sending...' : formButtonText}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </main>
    </div>
  );
};

export default Checkout;